'use client';

import * as React from 'react';

import { Eye20Filled, EyeOff20Filled } from '@fluentui/react-icons';
import { X } from 'lucide-react';
import type { TextFieldProps as AriaTextFieldProps } from 'react-aria-components';
import {
  TextField as AriaTextField,
  Label as AriaLabel,
  Input as AriaInput,
  TextArea as AriaTextArea,
} from 'react-aria-components';
import type { ClassValue } from 'tailwind-variants';
import { tv } from 'tailwind-variants';

import { floatingLabelStyles } from '@avodah-inc/ui/utils';

const textFieldVariants = tv({
  slots: {
    root: 'border border-gray-300 relative disabled:opacity-50 bg-white group px-4 py-2 flex items-center rounded gap-2 w-full data-[disabled=true]:cursor-not-allowed data-[invalid=true]:border-red data-[focus=true]:border-primary',
    input:
      'peer leading-6 placeholder:text-gray-500 bg-white box-border transition-colors disabled:opacity-50 data-[disabled=true]:text-gray-500 disabled:cursor-not-allowed focus-visible:outline-none w-full text-body-3 text-gray-900',
  },
  variants: {
    variant: {
      input: {
        root: 'h-10',
      },
      textarea: {
        root: '',
      },
    },
    size: {
      sm: {
        root: 'rounded-lg h-6 py-0',
        input: 'leading-5 text-sm',
      },
      md: {
        root: '',
      },
    },
  },
  defaultVariants: {
    variant: 'input',
    size: 'md',
  },
});

export type TextFieldProps = AriaTextFieldProps & {
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
  variant?: 'input' | 'textarea';
  placeholder?: string;
  onClick?: () => void;
  onPress?: () => void;
  classNames?: {
    root?: ClassValue;
    input?: ClassValue;
  };
  label?: string;
  visibilityToggle?: boolean;
  isOptional?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  size?: 'sm' | 'md';
  dataTestId?: string;
};

const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      label,
      rightIcon,
      leftIcon,
      classNames,
      clearable,
      onClear,
      variant = 'input',
      size = 'md',
      ...props
    },
    forwardedRef,
  ) => {
    const [value, setValue] = React.useState(props.defaultValue);
    const [isFocused, setIsFocused] = React.useState(false);
    const [inputType, setInputType] = React.useState(
      props.visibilityToggle ? 'password' : props.type,
    );

    React.useEffect(() => {
      setValue(props.value);
    }, [props.value]);

    const Component = variant === 'input' ? AriaInput : AriaTextArea;

    const { input: inputStyles, root: rootStyles } = textFieldVariants({
      size,
    });

    return (
      <AriaTextField
        className={rootStyles({
          className: classNames?.root,
          variant,
        })}
        value={value}
        data-placeholder-shown={value ? undefined : true}
        ref={forwardedRef}
        data-focus={isFocused}
        onFocusChange={(e) => {
          setIsFocused(e);
        }}
        type={inputType}
        {...props}
      >
        {leftIcon}
        {label && (
          <AriaLabel className={floatingLabelStyles}>
            {label}
            {props.isRequired ? <span className="text-red"> *</span> : null}
          </AriaLabel>
        )}
        <Component
          className={inputStyles({
            className: classNames?.input,
          })}
          placeholder={
            label
              ? `${label}${props.isRequired ? ' *' : ''}`
              : props.placeholder
          }
          onClick={() => {
            props.onClick?.();
            props.onPress?.();
          }}
          autoComplete="off"
          onChange={(e) => {
            setValue(e.target.value);
            props.onChange?.(e.target.value);
          }}
          data-testid={props.dataTestId}
        />
        {clearable && value && (
          <button
            onClick={() => {
              setValue('');
              onClear?.();
            }}
          >
            <X size={16} className="text-gray-400" />
          </button>
        )}
        {rightIcon}
        {props.visibilityToggle && (
          <>
            {inputType === 'password' ? (
              <EyeOff20Filled
                className="text-primary"
                onClick={() => {
                  setInputType('text');
                }}
              />
            ) : (
              <Eye20Filled
                className="text-primary"
                onClick={() => {
                  setInputType('password');
                }}
              />
            )}
          </>
        )}
      </AriaTextField>
    );
  },
);
TextField.displayName = 'TextField';

export { TextField, textFieldVariants };
